/**
 * Tenant-wide customer custom-field schema CRUD.
 *
 * The shape is identical to `service-detail.ts`'s `CustomField` types
 * (TEXT / TEXTAREA / NUMBER / SELECT / DATE / FILE / CHECKBOX) — we
 * re-export `CustomField`, `CustomFieldInput`, etc. from there so the
 * shared `<CustomFieldsManager />` UI binds to one type.
 *
 * Endpoints: `/api/v1/customer-fields/*` — see `CustomerCustomFieldController`.
 */
import { useQuery } from "@tanstack/react-query";
import { authFetch, authFetchJson } from "./auth-fetch";
import { throwIfNotOk } from "./api-error";
import { useAuthStore } from "./auth";
import type { CustomField, CustomFieldInput } from "./service-detail";

export type CustomerFieldsResponse = { fields: CustomField[] };

export function useCustomerCustomFields() {
  const accessToken = useAuthStore((s) => s.accessToken);
  return useQuery({
    queryKey: ["customer.custom-fields"],
    enabled: !!accessToken,
    queryFn: () =>
      authFetchJson<CustomerFieldsResponse>(`/api/v1/customer-fields`),
  });
}

export async function createCustomerCustomField(
  body: CustomFieldInput,
): Promise<CustomField> {
  return authFetchJson<CustomField>(`/api/v1/customer-fields`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export async function updateCustomerCustomField(
  fieldId: string,
  body: Partial<CustomFieldInput>,
): Promise<CustomField> {
  return authFetchJson<CustomField>(`/api/v1/customer-fields/${fieldId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export async function deleteCustomerCustomField(fieldId: string): Promise<void> {
  const res = await authFetch(`/api/v1/customer-fields/${fieldId}`, {
    method: "DELETE",
  });
  await throwIfNotOk(res);
}

export async function reorderCustomerCustomFields(ids: string[]): Promise<void> {
  const res = await authFetch(`/api/v1/customer-fields/reorder`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ids }),
  });
  await throwIfNotOk(res);
}
